import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  SxProps,
  Theme,
} from "@mui/material";
import CustomSkeleton from "../Loading/CustomSkeleton";

export interface CustomSummaryCardProps {
  /** カード上部のラベル */
  label: string;
  /** ラベル右側に表示するアイコン (例: トレンド矢印) */
  icon?: React.ReactNode;
  /** メインの値 (例: ¥302,000、100%)。ReactNode を渡せば部分的に異なるスタイルも可能 */
  value: React.ReactNode;
  /** サブテキスト or 要素。string なら値の右、ReactNode なら下に配置 (subPosition で上書き可) */
  sub?: React.ReactNode;
  /**
   * sub の配置:
   * - "inline" (デフォルト): 値の右に小さく表示 (KPI/契約率カード向け)
   * - "below": 値の下に表示 (Budget の年度累計カード向け / progress 指定時)
   */
  subPosition?: "inline" | "below";
  /** value の文字色 (デフォルト #3E3E3E) */
  valueColor?: string;
  /** sub の文字色 (デフォルト #999、subPosition="below" 時は無効) */
  subColor?: string;
  /** value のフォントサイズ (デフォルト "20px") */
  valueSize?: string;
  /** label のフォントサイズ (デフォルト "14px") */
  labelSize?: string;
  /** カード全体の追加 sx */
  sx?: SxProps<Theme>;
  /**
   * subPosition="inline" 時の sub の縦方向の揃え方。
   * - "baseline" (デフォルト): 値の baseline に sub の baseline を合わせる（短い文字列 sub 向け）
   * - "flex-start": 値の上端に sub の上端を合わせる（複数行/要素を含む sub 向け）
   * - "center": 値の中央に sub を合わせる
   */
  subAlign?: "baseline" | "flex-start" | "center";
  /**
   * 円グラフ進捗 (0-100)。指定すると value/sub の左側にリング表示。
   * 契約率/達成率カード向け。
   */
  progress?: number;
  /** 円グラフ中央に表示するラベル (デフォルト "{progress}%") */
  progressLabel?: string;
  /** 円グラフの色 (デフォルト #0B9DBD) */
  progressColor?: string;
  /** true の時、カード内を Skeleton 表示にする */
  loading?: boolean;
  /** Skeleton 表示までの遅延時間。短い loading のチラつきを抑える */
  loadingDelayMs?: number;
}

/**
 * KPI/サマリー用カード。
 *
 * - subPosition="inline" (デフォルト): ラベル + (値 + 右寄せ sub) の 2 行
 * - subPosition="below": ラベル(+icon) + 値 + 下に sub の 3 行
 *
 * レントロール、予実、物件詳細などで広く使用。
 */
const CustomSummaryCard: React.FC<CustomSummaryCardProps> = ({
  label,
  icon,
  value,
  sub,
  subPosition = "inline",
  valueColor,
  subColor,
  valueSize = "20px",
  labelSize = "14px",
  sx,
  progress,
  progressLabel,
  progressColor = "#0B9DBD",
  subAlign = "baseline",
  loading = false,
  loadingDelayMs = 0,
}) => {
  const [showSkeleton, setShowSkeleton] = useState(
    loading && loadingDelayMs <= 0,
  );
  const hasProgress = typeof progress === "number";
  const clamped = hasProgress
    ? Math.min(100, Math.max(0, progress as number))
    : 0;
  const parsedLabelSize = Number.parseInt(labelSize, 10) || 14;
  const parsedValueSize = Number.parseInt(valueSize, 10) || 20;
  const labelLineHeight = parsedLabelSize * 1.43;
  const valueLineHeight = parsedValueSize * 1.25;
  const shouldRenderLoadingLayout = showSkeleton;

  useEffect(() => {
    if (!loading) {
      setShowSkeleton(false);
      return;
    }

    if (loadingDelayMs <= 0) {
      setShowSkeleton(true);
      return;
    }

    const timer = window.setTimeout(() => {
      setShowSkeleton(true);
    }, loadingDelayMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [loading, loadingDelayMs]);

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "#fff",
        borderRadius: "8px",
        border: "1px solid #E0E0E0",
        p: 2,
        // リング分のスペースを右側に確保（値とリングの重なり防止）
        pr: hasProgress ? "72px" : 2,
        ...(sx || {}),
      }}
    >
      {shouldRenderLoadingLayout ? (
        <Box>
          <CustomSkeleton
            width="100%"
            height={labelLineHeight}
            containerSx={{
              width: "45%",
              mb: subPosition === "below" ? 1 : 0.5,
            }}
          />
          {subPosition === "inline" ? (
            <Stack
              direction="row"
              alignItems={subAlign}
              justifyContent="space-between"
              sx={{ minWidth: 0, flexWrap: "wrap", columnGap: 1, rowGap: 0.5 }}
            >
              <CustomSkeleton
                width="100%"
                height={valueLineHeight}
                containerSx={{ width: "65%", minWidth: 0 }}
              />
              <Box
                sx={{
                  fontSize: "14px",
                  lineHeight: "normal",
                  minWidth: 0,
                  flex: "0 1 auto",
                  maxWidth: "100%",
                }}
              >
                {"\u00A0"}
              </Box>
            </Stack>
          ) : (
            <>
              <CustomSkeleton
                width="100%"
                height={valueLineHeight}
                containerSx={{ width: "65%" }}
              />
              <CustomSkeleton
                width="100%"
                height={14}
                containerSx={{ width: "50%", mt: 0.5 }}
              />
            </>
          )}
        </Box>
      ) : (
        <>
          {/* ラベル + (オプション)アイコン */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: subPosition === "below" ? 1 : 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: labelSize,
                color: "#999",
                fontWeight: 500,
                minWidth: 0,
                overflowWrap: "anywhere",
              }}
            >
              {label}
            </Typography>
            {icon}
          </Box>

          {hasProgress && (
            // 絶対配置でカード右側に浮かせて、値/ラベルのレイアウトに影響させない
            <Box
              sx={{
                position: "absolute",
                top: "55%",
                right: 16,
                transform: "translateY(-50%)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <CircularProgress
                variant="determinate"
                value={100}
                size={50}
                thickness={4}
                sx={{ color: "#EEE" }}
              />
              <CircularProgress
                variant="determinate"
                value={clamped}
                size={50}
                thickness={4}
                sx={{
                  color: progressColor,
                  position: "absolute",
                  left: 0,
                  top: 0,
                  "& .MuiCircularProgress-circle": {
                    strokeLinecap: "round",
                  },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: "#3E3E3E",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {progressLabel ?? `${Math.round(clamped)}%`}
                </Typography>
              </Box>
            </Box>
          )}

          {subPosition === "inline" ? (
            <Stack
              direction="row"
              alignItems={subAlign}
              justifyContent="space-between"
              sx={{ minWidth: 0, flexWrap: "wrap", columnGap: 1, rowGap: 0.5 }}
            >
              <Typography
                className="summary-card-value"
                sx={{
                  fontSize: valueSize,
                  fontWeight: 700,
                  color: valueColor || "#3E3E3E",
                  fontVariantNumeric: "tabular-nums",
                  minWidth: 0,
                  lineHeight: 1.25,
                  overflowWrap: "anywhere",
                }}
              >
                {value}
              </Typography>
              <Box
                sx={{
                  fontSize: "14px",
                  color: subColor || "#999",
                  minWidth: 0,
                  flex: "0 1 auto",
                  maxWidth: "100%",
                  overflowWrap: "anywhere",
                }}
              >
                {sub || "\u00A0"}
              </Box>
            </Stack>
          ) : (
            <>
              <Typography
                className="summary-card-value"
                sx={{
                  fontSize: valueSize,
                  fontWeight: 700,
                  color: valueColor || "#3E3E3E",
                  fontVariantNumeric: "tabular-nums",
                  minWidth: 0,
                  lineHeight: 1.25,
                  overflowWrap: "anywhere",
                }}
              >
                {value}
              </Typography>
              {sub && <Box sx={{ mt: 0.5 }}>{sub}</Box>}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default CustomSummaryCard;
